import { Group, Skeleton, Stack, Table, Text, Title } from '@mantine/core';

/**
 * Заголовок экрана.
 *
 * Число рядом с названием — не украшение: менеджер, открывая ленту, первым
 * делом хочет знать масштаб («три дефекта» или «тридцать»), а не читать
 * таблицу, чтобы это выяснить.
 */
export function PageHeader({
  title,
  count,
  hint,
  action,
}: {
  title: string;
  count?: number | undefined;
  hint?: string | undefined;
  action?: React.ReactNode;
}): React.JSX.Element {
  return (
    <Stack gap={2} mb="lg">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" align="baseline">
          <Title order={1}>{title}</Title>
          {count !== undefined && (
            <Text className="pf-num" c="dimmed" fz="sm" fw={500}>
              {count}
            </Text>
          )}
        </Group>
        {action}
      </Group>
      {hint !== undefined && (
        <Text c="dimmed" fz="sm" maw="68ch">
          {hint}
        </Text>
      )}
    </Stack>
  );
}

/**
 * Пустое состояние.
 *
 * Пустой экран без объяснения читается как поломка. Здесь всегда сказано,
 * почему пусто, и хорошо это или плохо: «дефектов нет» — это успех флота,
 * а не отсутствие данных.
 */
export function EmptyState({
  title,
  hint,
  tone = 'neutral',
}: {
  title: string;
  hint?: string | undefined;
  tone?: 'neutral' | 'good';
}): React.JSX.Element {
  return (
    <Stack
      align="center"
      gap={6}
      py={64}
      style={{
        border: '1px dashed var(--pf-line-strong)',
        borderRadius: 'var(--mantine-radius-md)',
      }}
    >
      <Text fw={600} fz="md" {...(tone === 'good' ? { c: 'blue.7' } : {})}>
        {title}
      </Text>
      {hint !== undefined && (
        <Text c="dimmed" fz="sm" ta="center" maw="46ch">
          {hint}
        </Text>
      )}
    </Stack>
  );
}

/**
 * Скелет таблицы.
 *
 * Форма повторяет будущие строки, поэтому при загрузке ничего не прыгает.
 * Спиннер по центру пустого экрана сообщал бы только «ждите».
 */
export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }): React.JSX.Element {
  return (
    <Table className="pf-table">
      <Table.Tbody>
        {Array.from({ length: rows }, (_, r) => (
          <Table.Tr key={r}>
            {Array.from({ length: cols }, (__, c) => (
              <Table.Td key={c}>
                <Skeleton height={9} radius="xl" width={c === 0 ? '58%' : '38%'} />
              </Table.Td>
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
